/*  eslint-disable @typescript-eslint/no-explicit-any */ 
'use client'
import { FiTrash2, FiMinus, FiPlus, FiShoppingCart } from 'react-icons/fi'
import { useRouter } from 'next/navigation'
import { useLocale, useTranslations } from 'next-intl'
import { useState, useEffect, useMemo } from 'react'
import { useCartStore } from '@/stores/cartStore'
import { Link } from '@/i18n/routing'
import { v4 as uuidv4 } from 'uuid'
import { RiVisaLine, RiMastercardFill, RiPaypalLine } from 'react-icons/ri'
import DynamicPrice from '@/components/DynamicPrice'

// Delete button component
export function DeleteButton({ 
  itemId,
  productId,
  selectedColor
}: { 
  itemId: string
  productId: string
  selectedColor?: string
}) {
  const t = useTranslations('cart')
  const removeItem = useCartStore(state => state.removeItem)
  const [isAnimating, setIsAnimating] = useState(false)
  
  const handleRemove = () => {
    setIsAnimating(true)
    
    // Optimistic UI update with animation
    setTimeout(() => {
      // Prefer using itemId if available
      if (itemId) {
        removeItem(itemId)
      } else {
        removeItem(productId, selectedColor)
      }
      setIsAnimating(false)
    }, 300)
  }
  
  return (
    <button
      onClick={handleRemove}
      className={`text-status-error hover:text-status-error/80 transition p-1 ${
        isAnimating ? 'animate-pulse opacity-50' : ''
      }`}
      aria-label={t('item.removeAria', { product: 'item' })}
      disabled={isAnimating}
    >
      <FiTrash2 className="h-5 w-5" />
    </button>
  )
}

// Quantity control component
export function QuantityControl({ 
  itemId,
  productId, 
  selectedColor,
  currentQuantity
}: { 
  itemId: string
  productId: string
  selectedColor?: string
  currentQuantity: number
}) {
  const t = useTranslations('cart')
  const updateItemQuantity = useCartStore(state => state.updateItemQuantity)
  const [localQuantity, setLocalQuantity] = useState<number>(currentQuantity)
  
  // Get the product details to check kg_price
  const product = useMemo(() => {
    const initialProducts = useCartStore.getState().initialProducts || [];
    return initialProducts.find(p => p.id === productId);
  }, [productId]);
  
  const isKgPrice = product?.kg_price !== false;
  
  const formatQuantity = (quantity: number) => {
    return isKgPrice ? 
      `${quantity >= 1000 ? (quantity / 1000).toFixed(1) + 'kg' : quantity + 'g'}` : 
      `${quantity} ${quantity === 1 ? 'unit' : 'units'}`;
  };
  
  // Update local quantity when the prop changes
  useEffect(() => {
    setLocalQuantity(currentQuantity);
  }, [currentQuantity]);

  const handleIncrease = () => {
    const increment = isKgPrice ? 100 : 1;
    const newQuantity = localQuantity + increment;
    setLocalQuantity(newQuantity);
    updateItemQuantity(itemId || productId, newQuantity, selectedColor);
  };

  const handleDecrease = () => {
    const decrement = isKgPrice ? 100 : 1;
    const minQuantity = isKgPrice ? 100 : 1;
    if (localQuantity <= minQuantity) {
      // Ask if the user wants to remove the item
      if (window.confirm(t('item.confirmRemove'))) {
        updateItemQuantity(itemId || productId, 0, selectedColor);
      }
      return;
    }
    
    const newQuantity = localQuantity - decrement;
    setLocalQuantity(newQuantity);
    updateItemQuantity(itemId || productId, newQuantity, selectedColor);
  };

  return (
    <div className="flex items-center justify-center">
      <button
        onClick={handleDecrease}
        className="text-text-secondary hover:text-text-main transition p-1"
        aria-label={t('item.decreaseQuantity')}
        disabled={isKgPrice ? localQuantity <= 100 : localQuantity <= 1}
      >
        <FiMinus className="h-4 w-4" />
      </button>
      <div className="relative mx-2 flex items-center">
        <span className="text-text-main text-center w-20">{formatQuantity(localQuantity)}</span>
      </div>
      <button
        onClick={handleIncrease}
        className="text-text-secondary hover:text-text-main transition p-1"
        aria-label={t('item.increaseQuantity')}
      >
        <FiPlus className="h-4 w-4" />
      </button>
    </div>
  )
}

// Product link component
export function ProductLink({ 
  productId, 
  productName, 
  concentration,
  selectedColor
}: { 
  productId: string
  productName: string
  concentration: string
  selectedColor: string
}) {
  const t = useTranslations('cart')
  const router = useRouter()
  const locale = useLocale()
  const cartStore = useCartStore()
  const translatedSlugs = cartStore.translatedSlugs || {}
  const translatedNames = cartStore.translatedNames || {}
  
  // Use translated name if available
  const displayName = translatedNames[productId] || productName
  
  const handleClick = () => {
    // Get the appropriate slug based on locale
    let slug: string | undefined
    
    if (locale !== 'en') {
      // Try to get the translated slug first
      slug = translatedSlugs[productId]
    }
    
    // If no translated slug found, use the English slug from the product data
    if (!slug) {
      const product = cartStore.initialProducts?.find((p: { id: string }) => p.id === productId)
      slug = product?.slug
    }
    
    // Navigate to the product page using the slug if available, otherwise fallback to ID
    if (slug) {
      router.push(`/${locale}/products/${slug}`)
    } else {
      console.warn('Product slug not available, using ID as fallback')
      router.push(`/${locale}/products/${productId}`)
    }
  }
  
  return (
    <button
      onClick={handleClick}
      className="hover:text-primary-light transition text-left"
      aria-label={t('item.viewProduct', { product: displayName })}
    >
      <span className="font-medium text-text-main">
        {displayName}
      </span>
      <div className="text-sm text-text-secondary">
        {concentration} {selectedColor && ` - ${selectedColor}`}
      </div>
    </button>
  )
}

// Checkout button component
export function CheckoutButton() {
  const t = useTranslations('cart')
  const router = useRouter()
  const locale = useLocale()
  const [isProcessing, setIsProcessing] = useState(false)
  
  const handleCheckout = async () => {
    if (isProcessing) return
    
    // Instead of redirecting to login, proceed to checkout
    // The checkout page will handle authentication/guest checkout
    try {
      setIsProcessing(true)
      
      // Redirect to checkout page
      router.push(`/${locale}/checkout`)
    } catch (error) {
      console.error('Error during checkout:', error)
      setIsProcessing(false)
    }
  }
  
  return (
    <button
      onClick={handleCheckout}
      className={`w-full mb-4 bg-gradient-to-r from-primary to-primary-light text-bg-main font-medium rounded-full px-6 py-4
        transform transition-all duration-200
        ${isProcessing ? 
          'opacity-70 cursor-not-allowed' : 
          'hover:from-primary/90 hover:to-primary-light/90 hover:shadow-xl hover:shadow-primary/30 hover:-translate-y-0.5 active:translate-y-0'
        }`}
      disabled={isProcessing}
    >
      <span className="flex items-center justify-center">
        {isProcessing ? (
          <>
            <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-bg-main" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            {t('checkout.processing')}
          </>
        ) : (
          t('checkout.button')
        )}
      </span>
    </button>
  )
}

interface CartClientProps {
  initialProducts: any[];
  initialCartItems: any[];
  translatedSlugs?: Record<string, string>;
  translatedNames?: Record<string, string>;
  siteSettings: any; // Use the correct type if available
  vatNumber: string;
}

export function CartClient({ 
  initialProducts, 
  initialCartItems,
  translatedSlugs = {},
  translatedNames = {},
  siteSettings,
  vatNumber
}: CartClientProps) {
  const t = useTranslations('cart')
  const items = useCartStore(state => state.items)
  const syncError = useCartStore(state => state.syncError)
  
  // Initialize cart store with server data
  useEffect(() => {
    // Set initial products and translations regardless of cart items
    useCartStore.setState({ 
      initialProducts,
      translatedSlugs,
      translatedNames
    })
    
    if (initialCartItems && initialCartItems.length > 0) {
      // Transform server format to client format
      const cartItems = initialCartItems.map((item: any) => ({
        id: item.id || uuidv4(), // Use existing id or generate a new one
        productId: item.product_id,
        quantity: item.quantity,
        selectedColor: item.selected_color
      }))
      
      // Deduplicate cart items based on productId and selectedColor
      const uniqueCartItems: any[] = []
      const seen = new Set()
      
      cartItems.forEach(item => {
        const key = `${item.productId}-${item.selectedColor || 'default'}`
        if (!seen.has(key)) {
          seen.add(key)
          uniqueCartItems.push(item)
        } else {
          // If duplicate found, merge the quantities
          const existingIndex = uniqueCartItems.findIndex(
            existing => existing.productId === item.productId && 
                      (item.selectedColor === undefined 
                        ? existing.selectedColor === undefined 
                        : existing.selectedColor === item.selectedColor)
          )
          if (existingIndex !== -1) {
            uniqueCartItems[existingIndex].quantity += item.quantity
          }
        }
      })
      
      // Set items in store
      useCartStore.setState({ items: uniqueCartItems })
    }
  }, [initialCartItems, initialProducts, translatedSlugs, translatedNames])
  
  // Helper function to match cart items with products
  const getEnrichedCartItems = () => {
    return items.map(item => {
      const product = initialProducts.find(p => p.id === item.productId)
      return {
        ...item, // This will include the id field
        product
      }
    }).filter(item => item.product) // Filter out items with no matching product
  }
  
  const cartItems = getEnrichedCartItems()
  
  // Function to get translated product name
  const getProductName = (productId: string, defaultName: string) => {
    return translatedNames[productId] || defaultName;
  }
  
  // Update formatPriceWithUnit to use DynamicPrice for both per-gram and per-unit price displays
  function formatPriceWithUnit(item: any) {
    const product = item.product;
    if (!product) return '';

    // Check if product is priced per kg or per unit
    const isKgPrice = product.kg_price !== false;

    // Get base price per kg or per unit
    const basePricePerKg = calculatePricePerKg(
      item.quantity,
      product.price_chart,
      product.price_per_kg
    );

    // Add extra price from selected option if available
    let extraPrice = 0;
    if (item.selectedColor && product.options) {
      try {
        let options = [];
        if (typeof product.options === 'string') {
          options = JSON.parse(product.options);
        } else if (Array.isArray(product.options)) {
          options = product.options;
        }
        const selectedOption = options.find((opt: any) => opt.name === item.selectedColor);
        if (selectedOption && selectedOption.extraPrice) {
          extraPrice = selectedOption.extraPrice;
        }
      } catch (e) {
        console.error('Error parsing product options:', e);
      }
    }

    // Calculate final price
    const finalPricePerKg = basePricePerKg + extraPrice;

    if (isKgPrice) {
      // Price per gram in EUR
      const pricePerGramEur = finalPricePerKg / 1000;
      return (
        <span>
          <DynamicPrice basePrice={pricePerGramEur} vat_number={!siteSettings.is_b2b ? vatNumber : ''} vat_rate={0.25} forceRefresh={true} isB2B={siteSettings.is_b2b} />
          {t('item.pricePerKg')}
        </span>
      );
    } else {
      // Price per unit in EUR
      const pricePerUnitEur = finalPricePerKg;
      return (
        <span>
          {t('item.pricePerUnit')}
          <DynamicPrice basePrice={pricePerUnitEur} vat_number={!siteSettings.is_b2b ? vatNumber : ''} vat_rate={0.25} forceRefresh={true} isB2B={siteSettings.is_b2b} />
        </span>
      );
    }
  }
  
  // Helper function for price calculation
  function calculatePricePerKg(quantityGrams: number, priceChart: Record<string, number> | undefined, basePrice: number) {
    if (!priceChart) {
      return basePrice
    }

    const quantityKg = quantityGrams / 1000

    // Sort price ranges by their lower bound and parse them properly
    const ranges = Object.entries(priceChart).sort((a, b) => {
      const aLower = parseFloat(a[0].split('-')[0])
      const bLower = parseFloat(b[0].split('-')[0])
      return aLower - bLower
    })

    // Find applicable price tier
    for (const [range, price] of ranges) {
      const [lower, upper] = range.replace('kg', '').split('-').map(n => parseFloat(n.trim()))
      if (quantityKg >= lower && (!upper || quantityKg <= upper)) {
        return price
      }
    }

    return basePrice
  }
  
  if (cartItems.length === 0) {
    return (
      <div className="pt-[100px] max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <h1 className="text-3xl font-bold text-text-main mb-8">{t('hero.title')}</h1>
        <div className="bg-bg-secondary rounded-lg shadow-md shadow-border/20 p-10 text-center max-w-2xl mx-auto">
          <div className="flex justify-center mb-4">
            <div className="relative w-24 h-24 text-text-secondary opacity-50">
              <FiShoppingCart className="w-20 h-20" />
            </div>
          </div>
          <h2 className="text-2xl font-medium text-text-main mb-3">{t('empty.title', { defaultValue: 'Your cart is empty' })}</h2>
          <p className="text-xl text-text-secondary mb-8 max-w-md mx-auto">{t('empty.message')}</p>
          <Link 
            href="/products"
            className="inline-block bg-gradient-to-r from-primary to-primary-light text-bg-main font-medium rounded-full px-8 py-4 
              shadow-lg shadow-primary/20 hover:shadow-xl hover:shadow-primary/30 hover:-translate-y-0.5 
              transform transition-all duration-200 active:translate-y-0"
          >
            {t('empty.browseLink')}
          </Link>
        </div>
      </div>
    )
  }
  
  return (
    <div className="pt-[100px] max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
      <h1 className="text-3xl font-bold text-text-main mb-8">{t('hero.title')}</h1>
      
      {syncError && (
        <div className="bg-status-error/20 text-status-error p-4 rounded-lg mb-4">
          {t('error.syncing')}
        </div>
      )}
      
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2">
          <div className="bg-surface rounded-xl p-6">
            <div className="hidden lg:block">
              <table className="w-full">
                <thead>
                  <tr>
                    <th className="text-left py-4 text-text-secondary font-regular">{t('item.product')}</th>
                    <th className="text-center py-4 text-text-secondary font-regular">{t('item.quantity')}</th>
                    <th className="text-right py-4 text-text-secondary font-regular">{t('item.price')}</th>
                    <th className="py-4 w-10"></th>
                  </tr>
                </thead>
                <tbody>
                  {cartItems.map((item) => (
                    <tr key={item.id} className=" transition-colors">
                      <td className="py-7">
                        <div className="flex items-center">
                          <ProductLink
                            productId={item.productId}
                            productName={getProductName(item.productId, item.product.name)}
                            concentration={item.product.concentration}
                            selectedColor={item.selectedColor || ''}
                          />
                        </div>
                      </td>
                      <td className="py-7">
                        <div className="flex justify-center">
                          <QuantityControl
                            itemId={item.id}
                            productId={item.productId}
                            selectedColor={item.selectedColor}
                            currentQuantity={item.quantity}
                          />
                        </div>
                      </td>
                      <td className="py-7 text-right">
                        <span className="font-medium text-primary text-lg">
                          <DynamicPrice basePrice={(() => {
                            const product = item.product;
                            if (!product) return 0;
                            // Get base price per kg based on quantity
                            const basePricePerKg = calculatePricePerKg(
                              item.quantity,
                              product.price_chart,
                              product.price_per_kg
                            );
                            // Get extra price from selected option if available
                            let extraPrice = 0;
                            if (item.selectedColor && product.options) {
                              try {
                                let options = [];
                                if (typeof product.options === 'string') {
                                  options = JSON.parse(product.options);
                                } else if (Array.isArray(product.options)) {
                                  options = product.options;
                                }
                                // Find the selected option
                                const selectedOption = options.find((opt: any) => 
                                  opt.name === item.selectedColor
                                );
                                // Add extra price if option found
                                if (selectedOption && selectedOption.extraPrice) {
                                  extraPrice = selectedOption.extraPrice;
                                }
                              } catch (e) {
                                console.error('Error parsing product options:', e);
                              }
                            }
                            // Add extra price to base price
                            const finalPricePerKg = basePricePerKg + extraPrice;
                            // Check if product is priced per kg or per unit
                            const isKgPrice = product.kg_price !== false;
                            let basePrice;
                            if (isKgPrice) {
                              // Convert price per kg to price per gram and multiply by quantity in grams
                              basePrice = (finalPricePerKg / 1000) * item.quantity;
                            } else {
                              // For unit-based pricing, just use the price directly
                              basePrice = finalPricePerKg * item.quantity;
                            }
                            return basePrice;
                          })()} vat_number={!siteSettings.is_b2b ? vatNumber : ''} vat_rate={0.25} forceRefresh={true} isB2B={siteSettings.is_b2b} />
                        </span>
                        <div className="text-xs text-text-secondary mt-1">
                          {formatPriceWithUnit(item)}
                        </div>
                      </td>
                      <td className="py-7 text-right">
                        <DeleteButton
                          itemId={item.id}
                          productId={item.productId}
                          selectedColor={item.selectedColor}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="block lg:hidden">
              {cartItems.map((item) => (
                <div key={item.id} className="border-b border-text-main/10 pb-4 mb-4 last:mb-0 last:border-0 space-y-2">
                  <div className="flex justify-between">
                    <span className="text-text-secondary">{t('item.price')}</span>
                    <span className="font-medium text-primary text-lg">
                      <DynamicPrice basePrice={(() => {
                        const product = item.product;
                        if (!product) return 0;
                        const basePricePerKg = calculatePricePerKg(
                          item.quantity,
                          product.price_chart,
                          product.price_per_kg
                        );
                        let extraPrice = 0;
                        if (item.selectedColor && product.options) {
                          try {
                            const options =
                              typeof product.options === 'string'
                                ? JSON.parse(product.options)
                                : product.options;
                            const selectedOption = options.find((opt: any) => opt.name === item.selectedColor);
                            if (selectedOption?.extraPrice) extraPrice = selectedOption.extraPrice;
                          } catch (e) {
                            console.error('Error parsing product options:', e);
                          }
                        }
                        const finalPricePerKg = basePricePerKg + extraPrice;
                        const isKgPrice = product.kg_price !== false;
                        return isKgPrice
                          ? (finalPricePerKg / 1000) * item.quantity
                          : finalPricePerKg * item.quantity;
                      })()} vat_number={!siteSettings.is_b2b ? vatNumber : ''} vat_rate={0.25} forceRefresh={true} isB2B={siteSettings.is_b2b} />
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-text-secondary">{t('item.product')}</span>
                    <ProductLink
                      productId={item.productId}
                      productName={getProductName(item.productId, item.product.name)}
                      concentration={item.product.concentration}
                      selectedColor={item.selectedColor || ''}
                    />
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-text-secondary">{t('item.quantity')}</span>
                    <QuantityControl
                      itemId={item.id}
                      productId={item.productId}
                      selectedColor={item.selectedColor}
                      currentQuantity={item.quantity}
                    />
                    <DeleteButton
                      itemId={item.id}
                      productId={item.productId}
                      selectedColor={item.selectedColor}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
        
        <div className="bg-surface rounded-xl p-6 h-fit">
          <h2 className="text-xl font-bold text-text-main mb-6">{t('summary.title')}</h2>
          
          <div className="py-5 my-6">
            <div className="flex justify-between mb-2">
              <span className="text-text-secondary">{t('summary.subtotal')}</span>
              <span className="text-text-main font-medium">
                <DynamicPrice basePrice={cartItems.reduce((total, item) => {
                  const product = item.product;
                  if (!product) return total;
                  const basePricePerKg = calculatePricePerKg(
                    item.quantity,
                    product.price_chart,
                    product.price_per_kg
                  );
                  let extraPrice = 0;
                  if (item.selectedColor && product.options) {
                    try {
                      let options = [];
                      if (typeof product.options === 'string') {
                        options = JSON.parse(product.options);
                      } else if (Array.isArray(product.options)) {
                        options = product.options;
                      }
                      const selectedOption = options.find((opt: any) => opt.name === item.selectedColor);
                      if (selectedOption && selectedOption.extraPrice) {
                        extraPrice = selectedOption.extraPrice;
                      }
                    } catch (e) {
                      console.error('Error parsing product options:', e);
                    }
                  }
                  const finalPricePerKg = basePricePerKg + extraPrice;
                  const isKgPrice = product.kg_price !== false;
                  let basePrice;
                  if (isKgPrice) {
                    basePrice = (finalPricePerKg / 1000) * item.quantity;
                  } else {
                    basePrice = finalPricePerKg * item.quantity;
                  }
                  return total + basePrice;
                }, 0)} vat_number={!siteSettings.is_b2b ? vatNumber : ''} vat_rate={0.25} forceRefresh={true} isB2B={siteSettings.is_b2b} />
              </span>
            </div>
          </div>
          
          <div className="flex justify-between mb-8">
            <span className="text-lg text-text-main font-bold">{t('summary.total')}</span>
            <span className="text-lg text-primary font-bold">
              <DynamicPrice basePrice={cartItems.reduce((total, item) => {
                const product = item.product;
                if (!product) return total;
                const basePricePerKg = calculatePricePerKg(
                  item.quantity,
                  product.price_chart,
                  product.price_per_kg
                );
                let extraPrice = 0;
                if (item.selectedColor && product.options) {
                  try {
                    let options = [];
                    if (typeof product.options === 'string') {
                      options = JSON.parse(product.options);
                    } else if (Array.isArray(product.options)) {
                      options = product.options;
                    }
                    const selectedOption = options.find((opt: any) => opt.name === item.selectedColor);
                    if (selectedOption && selectedOption.extraPrice) {
                      extraPrice = selectedOption.extraPrice;
                    }
                  } catch (e) {
                    console.error('Error parsing product options:', e);
                  }
                }
                const finalPricePerKg = basePricePerKg + extraPrice;
                const isKgPrice = product.kg_price !== false;
                let basePrice;
                if (isKgPrice) {
                  basePrice = (finalPricePerKg / 1000) * item.quantity;
                } else {
                  basePrice = finalPricePerKg * item.quantity;
                }
                return total + basePrice;
              }, 0)} vat_number={!siteSettings.is_b2b ? vatNumber : ''} vat_rate={0.25} forceRefresh={true} isB2B={siteSettings.is_b2b} />
            </span>
          </div>
          
          <CheckoutButton />
          
          <div className="flex justify-center w-full mb-4">
            <div className="flex items-center justify-between w-full px-6 mt-3">
              <RiVisaLine className="w-20 h-20 text-text-secondary hover:text-text-main/80 transition" />
              <RiPaypalLine className="w-20 h-12 text-text-secondary hover:text-text-main/80 transition" />
              <RiMastercardFill className="w-20 h-16 text-text-secondary hover:text-text-main/80 transition" />
            </div>
          </div>
          
          <Link 
            href="/products"
            className="inline-block w-full text-center text-text-secondary hover:text-primary transition mt-4"
          >
            {t('empty.browseLink')}
          </Link>
        </div>
      </div>
    </div>
  )
}
